'use client'

import * as React from 'react'
import * as BasePhoneInput from 'react-phone-number-input'
import flags from 'react-phone-number-input/flags'

import { Combobox as ComboboxPrimitive } from '@base-ui-components/react/combobox'
import { useTranslations } from 'next-intl'

import { Button } from '@/components/ui/base-button'
import {
  Combobox,
  ComboboxEmpty,
  ComboboxIcon,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue
} from '@/components/ui/base-combobox'
import { Input } from '@/components/ui/base-input'
import { ScrollArea } from '@/components/ui/base-scroll-area'

import { cn } from '@/utils'

type PhoneInputSize = 'lg' | 'md' | 'sm'

const PhoneInputContext = React.createContext<{
  popupClassName?: string
  scrollAreaClassName?: string
  variant: PhoneInputSize
}>({
  popupClassName: undefined,
  scrollAreaClassName: undefined,
  variant: 'md'
})

type CountryEntry = { label: string; value: BasePhoneInput.Country | undefined }

type CountrySelectProps = {
  disabled?: boolean
  onChange: (country: BasePhoneInput.Country) => void
  options: CountryEntry[]
  value: BasePhoneInput.Country
}

type PhoneInputProps = {
  onChange?: (value: BasePhoneInput.Value) => void
  popupClassName?: string
  scrollAreaClassName?: string
  variant?: PhoneInputSize
} & Omit<
  BasePhoneInput.Props<typeof BasePhoneInput.default>,
  'onChange' | 'popupClassName' | 'scrollAreaClassName' | 'variant'
> &
  Omit<React.ComponentProps<'input'>, 'onChange' | 'ref' | 'value'>

function CountrySelect({
  disabled,
  onChange,
  options: countryList,
  value: selectedCountry
}: CountrySelectProps) {
  const { popupClassName, scrollAreaClassName, variant } =
    React.useContext(PhoneInputContext)
  const t = useTranslations('components.form.phoneInput')
  const tUi = useTranslations('components.ui.phoneInput')
  const [searchValue, setSearchValue] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)

  const [portalContainer, setPortalContainer] =
    React.useState<HTMLElement | null>(null)

  const filteredCountries = React.useMemo(() => {
    if (!searchValue) return countryList
    return countryList.filter(({ label }) =>
      label.toLowerCase().includes(searchValue.toLowerCase())
    )
  }, [countryList, searchValue])

  // Focus the input when it becomes available and handle input events
  React.useEffect(() => {
    const input = inputRef.current
    if (!input) return

    // Use a small delay to ensure the popup is fully rendered
    const timer = setTimeout(() => {
      input.focus()
    }, 100)

    // Also handle input events directly on the element as a fallback
    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement
      if (target && target.value !== searchValue) {
        setSearchValue(target.value)
      }
    }

    input.addEventListener('input', handleInput)

    return () => {
      clearTimeout(timer)
      input.removeEventListener('input', handleInput)
    }
  }, [searchValue])

  /**
   * FIX for Focus Trap in Dialogs:
   *
   * When this Combobox is used inside a Radix UI Dialog, the Dialog's FocusScope
   * traps focus within the dialog content. Standard Portals (like those used by
   * Base UI or Radix by default) render the popup into `document.body`.
   * Since `document.body` is outside the Dialog's DOM tree, the focus trap prevents
   * interaction with the input inside the popup (clicks are blocked, focus is stolen back).
   *
   * To solve this, we use a "Local Portal" pattern:
   * 1. Create a `ref` to a local div (`portalContainer`) rendered inside this component.
   * 2. Pass this container to `<ComboboxPrimitive.Portal>`.
   * 3. This renders the popup physically inside the Dialog's DOM tree (satisfying the focus trap).
   * 4. We still wrap it in `<ComboboxPrimitive.Positioner>` to satisfy Base UI's structural requirements.
   */
  return (
    <Combobox
      items={filteredCountries}
      onValueChange={(country) => {
        if (country) {
          onChange(country as BasePhoneInput.Country)
        }
      }}
      value={selectedCountry || ''}
    >
      <div className="relative">
        <ComboboxTrigger
          render={
            <Button
              className={cn(
                [
                  'flex h-[42px] w-16 gap-1 rounded-s-md rounded-e-none border-e-0 !px-3.5 !py-2 focus:z-10',
                  'shadow-xs shadow-black/5'
                ],
                disabled && 'opacity-50'
              )}
              disabled={disabled}
              mode="input"
              size={variant}
              variant="outline"
            >
              <ComboboxValue>
                {(value: BasePhoneInput.Country) => (
                  <>
                    <FlagComponent country={value} countryName={value} />
                  </>
                )}
              </ComboboxValue>
              <ComboboxIcon />
            </Button>
          }
        />
        <div ref={setPortalContainer} />
        {portalContainer && (
          <ComboboxPrimitive.Portal container={portalContainer}>
            <ComboboxPrimitive.Positioner
              align="start"
              className="pointer-events-auto z-[10000] outline-none"
              side="bottom"
              sideOffset={4}
            >
              <ComboboxPrimitive.Popup
                className={cn(
                  'bg-popover mt-1 w-[300px] overflow-hidden rounded-md border shadow-md',
                  'data-[state=closed]:hidden',
                  popupClassName
                )}
              >
                <ComboboxInput
                  className={cn([
                    'pointer-events-auto rounded-none border-0 shadow-none',
                    'focus-visible:border-border focus-visible:ring-0 focus-visible:ring-offset-0'
                  ])}
                  onChange={(e) => {
                    // Update search value - don't stop propagation to allow combobox to handle it
                    const value = e.target.value
                    setSearchValue(value)
                  }}
                  onFocus={(e) => {
                    // Ensure focus events work
                    e.stopPropagation()
                  }}
                  onKeyDown={(e) => {
                    // Allow all keyboard input to work normally
                    // Only stop propagation for Escape to prevent dialog from closing
                    if (e.key === 'Escape') {
                      e.stopPropagation()
                    }
                  }}
                  onKeyDownCapture={(e) => {
                    // Stop propagation during capture phase to ensure dialog doesn't intercept keys
                    e.stopPropagation()
                  }}
                  onMouseDown={(e) => {
                    // Allow the input to receive focus
                    e.stopPropagation()
                  }}
                  placeholder={t('examplePlaceholder')}
                  ref={inputRef}
                  value={searchValue}
                  variant={variant}
                />
                <ComboboxSeparator />
                <ComboboxEmpty className="px-2 pt-4 pb-2 text-center">
                  {tUi('noCountryFound')}
                </ComboboxEmpty>
                <div
                  className="pointer-events-auto"
                  onTouchMove={(e) => e.stopPropagation()}
                  onWheel={(e) => e.stopPropagation()}
                >
                  <ScrollArea
                    className={cn(
                      'h-[300px]',
                      '[&_[data-slot=scroll-area-viewport]]:pointer-events-auto',
                      '[&_[data-slot=scroll-area-viewport]]:touch-auto',
                      scrollAreaClassName
                    )}
                  >
                    <ComboboxList className="my-1 overflow-hidden">
                      {filteredCountries.map(({ label, value }) =>
                        value ? (
                          <ComboboxItem
                            className="flex items-center gap-2 ps-3 pe-8"
                            key={value}
                            value={value}
                          >
                            <FlagComponent
                              country={value}
                              countryName={label}
                            />
                            <span className="flex-1 text-sm">{label}</span>
                            <span className="text-foreground/50 text-sm">
                              {`+${BasePhoneInput.getCountryCallingCode(value)}`}
                            </span>
                            <ComboboxItemIndicator className="start-auto end-2.5" />
                          </ComboboxItem>
                        ) : null
                      )}
                    </ComboboxList>
                  </ScrollArea>
                </div>
              </ComboboxPrimitive.Popup>
            </ComboboxPrimitive.Positioner>
          </ComboboxPrimitive.Portal>
        )}
      </div>
    </Combobox>
  )
}

function FlagComponent({ country, countryName }: BasePhoneInput.FlagProps) {
  const Flag = flags[country]

  return (
    <span className="bg-foreground/10 flex h-4 w-6 overflow-hidden rounded [&_svg:not([class*='size-'])]:size-full!">
      {Flag && <Flag title={countryName} />}
    </span>
  )
}

function InputComponent({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  const { variant } = React.useContext(PhoneInputContext)
  return (
    <Input
      className={cn(
        'h-[42px] rounded-s-none rounded-e-xl !px-3.5 !py-2 focus:z-1',
        className
      )}
      variant={variant}
      {...props}
    />
  )
}

function PhoneInput({
  className,
  onChange,
  popupClassName,
  scrollAreaClassName,
  value,
  variant,
  ...props
}: PhoneInputProps) {
  const phoneInputSize = variant || 'md'
  return (
    <PhoneInputContext.Provider
      value={{ popupClassName, scrollAreaClassName, variant: phoneInputSize }}
    >
      <BasePhoneInput.default
        className={cn(
          'flex',
          props['aria-invalid'] &&
            '[&_*[data-slot=combobox-trigger]]:border-destructive [&_*[data-slot=combobox-trigger]]:ring-destructive/50',
          className
        )}
        countrySelectComponent={CountrySelect}
        flagComponent={FlagComponent}
        inputComponent={InputComponent}
        onChange={(value) => onChange?.(value || ('' as BasePhoneInput.Value))}
        smartCaret={false}
        value={value || undefined}
        {...props}
      />
    </PhoneInputContext.Provider>
  )
}

export { PhoneInput }
